import { IPromise } from 'angular';
import { StateParams } from 'angular-ui-router';
import DjangoClient, {
    IPagedResult,
} from '../../tn.django/django-client.service/djangoClient';
import { IDjangoFontFamily, IDjangoFontStyle } from '../classes';
import {
    FontCategory,
    FontLanguage,
    FontPosture,
    FontRecommededFunction,
    FontSort,
    FontVarAxis,
    FontWeight,
    FontWidth,
    isFontCategory,
    isFontLanguage,
    isFontPosture,
    isFontRecommededFunction,
    isFontVarAxis,
    isFontWeight,
    isFontWidth,
} from '../constants';

// params common to both styles and families
export interface FontListParams {
    // common to both styles and families
    designers?: number[];
    foundry?: number[];
    id?: number[];
    page?: number; // pages start at 1.
    page_size?: number; // number of items per page
    posture?: FontPosture[];
    slug?: string;
    tn_weight_min?: number;
    tn_weight_max?: number;
    tn_width_min?: number;
    tn_width_max?: number;
    variable?: FontVarAxis[];

    // styles only
    family?: number[];
    weight?: FontWeight[];
    width?: FontWidth[];

    // families only
    family_slug?: string[]; // only used on the series page, client side only
    categories?: FontCategory[];
    language?: FontLanguage[];
    recommended_function?: FontRecommededFunction[];
    search?: string;
    sort?: FontSort;
}

export const FONT_LIST_PARAMS_STATE_PARAMS_URL_PART =
    '?name?filter_category?filter_width_range?filter_weight_range?filter_variable?filter_recommended_function?filter_posture?filter_language?filter_designer?page?page_size?sort?weight?width' as const;
export const FONT_LIST_PARAMS_STATE_PARAMS = {
    name: {
        // string used to filter family name
        dynamic: true,
        value: undefined,
        squash: true,
    },
    // TODO: add filter_{name} in parallel with just {name} and let {name} take precedence over filter_{name}
    // it would be nice id {name} were the same as what we pass to the rest api call.
    filter_category: {
        // comma separated list of category ids
        // 0 - Sans, 1- Serif, 2-Symbol,  3-Slab Serif, 4-Wacky, 5-Script, 6-Decorative
        dynamic: true,
        value: undefined,
        squash: true,
    },
    filter_width_range: {
        // comma separated string of min,max
        // ex) 200,900
        dynamic: true,
        value: '0,900',
        squash: true,
    },
    filter_weight_range: {
        // comma separated string of min,max
        // ex) 200,900
        dynamic: true,
        value: '0,900',
        squash: true,
    },
    filter_variable: {
        // comma separated list of variable axes
        // ital, opsz, slnt, wght, wdth, custom
        dynamic: true,
        value: undefined,
        squash: true,
    },
    filter_recommended_function: {
        // comma separated list of recommended use ids
        // 0 - Display, 1 - Headlines, 2 - Subhead, 3 - Poster, 4 - Text, 5 - Body, 6 - Initials, 7 - Agate, 8  - Captions, 9 - Titling, 10 - Code, 11- Signage
        dynamic: true,
        value: undefined,
        squash: true,
    },
    filter_posture: {
        // comma separated string of posture ids
        // 0 - Roman, 1 - Italic, variable - Variable
        dynamic: true,
        value: undefined,
        squash: true,
    },
    filter_language: {
        // comma separated list of script ids
        // 1 - Wester European, 2 - Central European, 3 - Northern European, 4 - Cyrillic, 5 - Turkish, 6 - Greek, 7 - Hebrew, 8 - Arabic, 9 - Vietnamese
        dynamic: true,
        value: undefined,
        squash: true,
    },
    filter_designer: {
        // comma separated list of designer ids
        // see api primary keys for designers
        dynamic: true,
        value: undefined,
        squash: true,
    },
    page: {
        // current page
        dynamic: true,
        value: '1',
        squash: true,
    },
    page_size: {
        // number of items per page
        dynamic: true,
        value: '20',
        squash: true,
    },
    sort: {
        // sort order
        // released, -released, name, -name
        dynamic: true,
        value: '-released',
        squash: true,
    },
    weight: {
        dynamic: true,
        value: undefined,
        squash: true,
    },
    width: {
        dynamic: true,
        value: undefined,
        squash: true,
    },
} as const;

function parseIntOrString(value: string): number | string {
    const parsed = parseInt(value);
    return isNaN(parsed) ? value : parsed;
}

// standard mapping of state params to font list params
export function FontListParamsStateParamsResolver(
    $stateParams: Partial<StateParams>
): FontListParams {
    // the defaults should come from the ui-router state params definition.
    const sort = $stateParams.sort || FONT_LIST_PARAMS_STATE_PARAMS.sort.value;
    const search =
        $stateParams.name || FONT_LIST_PARAMS_STATE_PARAMS.name.value;
    const page = parseInt(
        $stateParams.page || FONT_LIST_PARAMS_STATE_PARAMS.page.value
    );
    const page_size = parseInt(
        $stateParams.page_size || FONT_LIST_PARAMS_STATE_PARAMS.page_size.value
    );
    const categories =
        $stateParams.filter_category
            ?.split(',')
            .map((n) => parseInt(n))
            .filter(isFontCategory) || [];
    const designers =
        $stateParams.filter_designer
            ?.split(',')
            .map((n) => parseInt(n))
            .filter(Boolean) || [];

    const family_slug = $stateParams.family?.split(',').filter(Boolean) || [];
    const language =
        $stateParams.filter_language
            ?.split(',')
            .map((n) => parseInt(n))
            .filter(isFontLanguage) || [];

    const posture =
        $stateParams.filter_posture
            ?.split(',')
            .map(parseIntOrString)
            .filter(isFontPosture) || [];
    const recommended_function =
        $stateParams.filter_recommended_function
            ?.split(',')
            .map((n) => parseInt(n))

            .filter(isFontRecommededFunction) || [];
    const tn_weight_range = (
        $stateParams.filter_weight_range ||
        FONT_LIST_PARAMS_STATE_PARAMS.filter_weight_range.value
    )
        .split(',')
        .map((n) => parseInt(n));

    const [tn_weight_min = 0, tn_weight_max = 900] = tn_weight_range;

    const tn_width_range = (
        $stateParams.filter_width_range ||
        FONT_LIST_PARAMS_STATE_PARAMS.filter_width_range.value
    )
        .split(',')
        .map((n) => parseInt(n));

    const [tn_width_min = 0, tn_width_max = 900] = tn_width_range;
    const variable =
        $stateParams.filter_variable?.split(',').filter(isFontVarAxis) || [];
    const weight =
        $stateParams.weight
            ?.split(',')
            .map(parseIntOrString)
            .filter(isFontWeight) || [];
    const width =
        $stateParams.width
            ?.split(',')
            .map(parseIntOrString)
            .filter(isFontWidth) || [];

    const fontListParams: FontListParams = {
        categories,
        designers,
        family_slug,
        language,
        page,
        page_size,
        posture,
        recommended_function,
        search,
        sort,
        tn_weight_min,
        tn_weight_max,
        tn_width_min,
        tn_width_max,
        variable,
        weight,
        width,
    };

    return fontListParams;
}

export function FontListParamsToStateParams(
    params: FontListParams
): Partial<StateParams> {
    const filter_category = params.categories?.filter(isFontCategory).join(',');
    const filter_designer = params.designers?.join(',');
    const filter_language = params.language?.filter(isFontLanguage).join(',');
    const filter_posture = params.posture?.filter(isFontPosture).join(',');
    const filter_recommended_function = params.recommended_function
        ?.filter(isFontRecommededFunction)
        .join(',');
    const filter_weight_range = `${params.tn_weight_min || '0'},${
        params.tn_weight_max || '900'
    }`;
    const filter_width_range = `${params.tn_width_min || '0'},${
        params.tn_width_max || '900'
    }`;
    const filter_variable = params.variable?.filter(isFontVarAxis).join(',');
    const name = params.search;
    const page = params.page?.toString();
    const page_size = params.page_size?.toString();
    const sort = params.sort;
    const weight = params.weight?.filter(isFontWeight).join(',');
    const width = params.width?.filter(isFontWidth).join(',');

    const stateParams: Partial<StateParams> = {
        filter_category,
        filter_designer,
        filter_language,
        filter_posture,
        filter_recommended_function,
        filter_variable,
        filter_weight_range,
        filter_width_range,
        name,
        page,
        page_size,
        sort,
        weight,
        width,
    };
    return stateParams;
}

export abstract class FontRepository {
    protected baseUri = '/api/1/styles/';

    static $inject = ['djangoClient'];
    constructor(protected djangoClient: DjangoClient) {}

    // filters common to both family and style.
    protected _filtersToParams(filters: FontListParams): URLSearchParams {
        const params = new URLSearchParams();
        filters.designers?.forEach((i) =>
            params.append('designer', i.toString())
        );
        filters.foundry?.forEach((i) => params.append('foundry', i.toString()));
        if (filters.id) params.append('id', filters.id.join(','));
        if (filters.language)
            filters.language.forEach((i) =>
                params.append('supported_languages', i.toString())
            );
        if (filters.page) params.set('page', filters.page.toString());
        if (filters.page_size)
            params.set('page_size', filters.page_size.toString());
        filters.posture?.forEach((i) => params.append('posture', i.toString()));
        if (filters.slug) params.set('slug', filters.slug);
        if (filters.tn_weight_min)
            params.set('tn_weight_min', filters.tn_weight_min.toString());
        if (filters.tn_weight_max)
            params.set('tn_weight_max', filters.tn_weight_max.toString());
        if (filters.tn_width_min)
            params.set('tn_width_min', filters.tn_width_min.toString());
        if (filters.tn_width_max)
            params.set('tn_width_max', filters.tn_width_max?.toString());
        if (filters.variable)
            filters.variable.forEach((i) =>
                params.append('variable_axes_filters', i)
            );
        return params;
    }

    public get(id: number): IPromise<IDjangoFontStyle | IDjangoFontFamily> {
        const url = this.baseUri + id;
        return this.djangoClient.get<IDjangoFontStyle>(url);
    }

    // https://api.typenetwork.com/api/1/families/recommended_size=700&recommended_size=800
    public list(
        filters: FontListParams
    ): IPromise<IPagedResult<IDjangoFontStyle | IDjangoFontFamily>> {
        const params = this._filtersToParams(filters);
        const queryString = params.toString();
        const url = `${this.baseUri}${queryString && `?${queryString}`}`;
        return this.djangoClient.getPaged<IDjangoFontStyle | IDjangoFontFamily>(
            url
        );
    }
}
